import { useContext, useEffect } from 'react';
import {
  ChevronRightIcon,
  Languages,
  LogOut,
  Moon,
  SettingsIcon,
  Sun,
  UserCog,
  Wrench,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ThemeContext, ThemeModes } from '@/providers/ThemeContextProvider';
import { LanguageContext, locales } from '@/providers/LanguageContextProvider';
import { logout } from '@/utils/logout';
import i18n from '@/utils/i18n';
import { isDev, isLocalDev } from '@/utils/isDev';
import {
  IsVytalEmployee,
  UpdateViewerContext,
  Viewer,
} from '@/providers/ViewerContextProvider';
import { RoleToClaim } from '@/contants';
import { allCapsToNormal } from '@/utils/stringHelper';
import createPersistedState from '@/hooks/use-persisted-state';
import { useIsPublicPage } from '@/utils/publicPage';
import { useIsMobile } from '@/hooks/use-mobile';

const persistedRoleOverwrite = createPersistedState('persistedRoleOverwrite');

interface SidebarUserMenuProps {
  viewer: Viewer;
  onOpenDevTools?: () => void;
  /**
   * 'sidebar' (default): rendered inside the sidebar footer.
   * 'compact': a plain icon button, used in the mobile public top header where
   * the sidebar collapses into an unopenable offcanvas drawer — this keeps the
   * language / theme menu reachable there.
   */
  variant?: 'sidebar' | 'compact';
}

export function SidebarUserMenu({
  viewer,
  onOpenDevTools,
  variant = 'sidebar',
}: SidebarUserMenuProps) {
  const { isLightMode, setColorMode, theme } = useContext(ThemeContext);
  const languageContext = useContext(LanguageContext);
  const updateViewer = useContext(UpdateViewerContext);
  const isPublicPage = useIsPublicPage();

  const isMobile = useIsMobile();

  const isDevelopmentEnv = isDev() || isLocalDev();
  const isVytalEmployee = IsVytalEmployee(viewer);
  const [selectedRole, setSelectedRole] = persistedRoleOverwrite(viewer.role);
  const selectedRoleLabel =
    typeof selectedRole === 'string' ? selectedRole : viewer.role;

  useEffect(() => {
    if (!selectedRoleLabel || !isVytalEmployee) {
      return;
    }

    updateViewer({
      ...viewer,
      role: selectedRoleLabel,
      roleSetManually: true,
    });
    // Keep behavior aligned with the previous top bar role overwrite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const languageItems = [
    { label: i18n.t('Common:english'), value: locales.en },
    { label: i18n.t('Common:german'), value: locales.de },
  ];

  const roleItems = Object.keys(RoleToClaim).map((key) => ({
    label: allCapsToNormal(key),
    value: key,
  }));

  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
    updateViewer({ ...viewer, role: value, roleSetManually: true });
  };

  const trigger =
    variant === 'compact' ? (
      <Button
        variant="ghost"
        size="icon"
        aria-label={i18n.t('Common:language')}
        className="tw:size-9"
      >
        <SettingsIcon className="tw:size-4" />
      </Button>
    ) : (
      <SidebarMenuButton
        size="lg"
        className="tw:cursor-pointer tw:gap-2 tw:justify-between"
      >
        <div className="tw:flex tw:items-center tw:gap-2">
          <div className="tw:size-8 tw:text-sidebar-primary-foreground tw:bg-sidebar-primary tw:p-2.5 tw:rounded-lg tw:flex tw:items-center tw:justify-center">
            <SettingsIcon className="tw:size-4" />
          </div>
          <span>{allCapsToNormal(selectedRoleLabel) || 'Vytal'}</span>
        </div>
        <ChevronRightIcon className="tw:size-4" />
      </SidebarMenuButton>
    );

  const dropdown = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        side={isMobile ? 'top' : 'right'}
        align={isMobile ? 'start' : 'end'}
        sideOffset={isMobile ? 16 : 0}
        className="tw:w-56"
      >
        {isDevelopmentEnv && onOpenDevTools && (
          <DropdownMenuItem
            onClick={onOpenDevTools}
            className="tw:cursor-pointer"
          >
            <Wrench
              className="tw:mr-2 tw:size-4"
              style={{
                color: isLocalDev() ? theme.color.danger : theme.color.primary,
              }}
            />
            <span>Dev Tools</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="tw:cursor-pointer">
            <Languages className="tw:mr-2 tw:size-4" />
            <span>{i18n.t('Common:language')}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {languageItems.map((item) => (
              <DropdownMenuItem
                key={item.value}
                onClick={() => {
                  languageContext.setLocale(item.value);
                }}
                className={cn(
                  'tw:cursor-pointer',
                  item.value === languageContext.locale && 'tw:bg-accent',
                )}
              >
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {isVytalEmployee && !isPublicPage && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="tw:cursor-pointer">
              <UserCog className="tw:mr-2 tw:size-4" />
              <span>{i18n.t('Common:selectRole')}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="tw:max-h-64 tw:overflow-y-auto">
              {roleItems.map((item) => (
                <DropdownMenuItem
                  key={item.value}
                  onClick={() => handleRoleChange(item.value)}
                  className={cn(
                    'tw:cursor-pointer',
                    item.value === selectedRole && 'tw:bg-accent',
                  )}
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        <DropdownMenuItem
          onClick={() =>
            setColorMode(isLightMode ? ThemeModes.Dark : ThemeModes.Light)
          }
          className="tw:cursor-pointer"
        >
          {isLightMode ? (
            <>
              <Moon className="tw:mr-2 tw:size-4" />
              <span>{i18n.t('Common:darkMode')}</span>
            </>
          ) : (
            <>
              <Sun className="tw:mr-2 tw:size-4" />
              <span>{i18n.t('Common:lightMode')}</span>
            </>
          )}
        </DropdownMenuItem>

        {!isPublicPage && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                localStorage.removeItem('persistedRoleOverwrite');
                logout();
              }}
              className="tw:cursor-pointer tw:text-destructive tw:focus:text-destructive"
            >
              <LogOut className="tw:mr-2 tw:size-4" />
              <span>{i18n.t('Common:logout')}</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (variant === 'compact') {
    return dropdown;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>{dropdown}</SidebarMenuItem>
    </SidebarMenu>
  );
}
